import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GetTasksPayload } from '@/domains/tasks/store/types';
import type { CreateTaskSteps, TaskTypeListResponse } from '@/domains/tasks/types/createTaskSliceTypes';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import TaskEvents from '@/domains/tasks/events';
import {
  createTask,
  getCreateTaskClaimants,
  getCreateTaskClaimantsSuccess,
  getCreateTaskClaims,
  getCreateTaskClaimsSuccess,
  getTaskTypeOptions,
  getTaskTypeOptionsSuccess,
  resetCreateTaskState,
  selectCreateTaskSteps,
  setCreateTaskOptionsLoading,
} from '@/domains/tasks/store/createTaskSlice';
import { getTasks } from '@/domains/tasks/store/tasksSlice';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

function dispatchUpdatedEntityEvent({
  claimSetId,
  entityType,
  entityKey,
}: {
  claimSetId: number | string;
  entityType: string;
  entityKey: string | null;
}) {
  try {
    if (entityType === EdsEntityCodes.ClaimSet && entityKey !== null)
      return EventService.dispatch(
        new TaskEvents.TaskCreated({
          entityType: EdsEntityCodes.ClaimSet,
          entityKey: String(claimSetId),
        }),
      );
    else if (entityType === EdsEntityCodes.ClaimEntity && entityKey !== null)
      return EventService.dispatch(
        new TaskEvents.TaskCreated({ entityType: EdsEntityCodes.ClaimEntity, entityKey: String(entityKey) }),
      );
    else if (entityType === EdsEntityCodes.Claimant && entityKey !== null)
      return EventService.dispatch(
        new TaskEvents.TaskCreated({ entityType: EdsEntityCodes.Claimant, entityKey: String(entityKey) }),
      );
  } catch (error) {
    console.error(error);
  }
}

function* createTaskSaga() {
  const claimSetId = yield select(selectCurrentClaimSetId);
  const task: CreateTaskSteps = yield select(selectCreateTaskSteps);

  try {
    if (!task.taskEntity.type || !task.taskEntity.id || !task.taskType?.id) {
      return;
    }

    yield put(setSystemLoadingStatus(true));

    yield call(HttpService.post, `/examiner/tasks/add`, {
      taskTypeId: +task.taskType?.id,
      entityId: +task.taskEntity.id,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: `Task "${task.taskType?.name}" for ${task.taskEntity.name} has been created successfully.`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    const taskType = task.taskEntity.type.replace('Entity', '');

    const actionPayload: GetTasksPayload = {
      section: EdsDefinitionSections.SidePanel,
      filters: [
        {
          propertyName: taskType[0].toUpperCase() + taskType.slice(1) + 'Id',
          values: [task.taskEntity.id],
        },
      ],
    };

    yield put(getTasks(actionPayload));

    dispatchUpdatedEntityEvent({ claimSetId, entityType: task.taskEntity.type, entityKey: task.taskEntity.id });
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetCreateTaskState());
    ModalService.close(Modals.AddTask);
  }
}

function* getCreateTaskClaimsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setCreateTaskOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).reduce(
      (acc, claimEntity) => {
        if (claimEntity?.virtualFields?.isDeleted) return acc;

        return [
          ...acc,
          { label: claimEntity.fields.claimNumber?.value?.toString() || '', value: claimEntity.id.toString() },
        ];
      },
      [] as SelectOption[],
    );

    if (data) {
      yield put(getCreateTaskClaimsSuccess(claimOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateTaskOptionsLoading(false));
  }
}

function* getCreateTaskClaimantsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setCreateTaskOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities.Claimants`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimantOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).flatMap(
      (claim) =>
        (claim.entities.Claimants as NormalizedV2Response[])?.map((claimant) => ({
          label: `${claimant.fields.claimantName?.value} (${claim.fields.claimNumber?.value || 'unknown'})`,
          value: claimant.id.toString(),
        })) || [],
    );

    if (data) {
      yield put(getCreateTaskClaimantsSuccess(claimantOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateTaskOptionsLoading(false));
  }
}

function* getTaskTypeOptionsSaga() {
  try {
    yield put(setCreateTaskOptionsLoading(true));

    const requestUrl = `/filter/${EdsEntityCodes.TaskType}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 1000 })}`;

    const { data }: HttpServiceResponse<TaskTypeListResponse> = yield call(HttpService.post, requestUrl, [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    if (data) {
      yield put(getTaskTypeOptionsSuccess({ listings: normalizedResponse }));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateTaskOptionsLoading(false));
  }
}

export function* createTaskWatcher() {
  yield takeLatest(createTask.type, createTaskSaga);
  yield takeLatest(getCreateTaskClaims.type, getCreateTaskClaimsSaga);
  yield takeLatest(getCreateTaskClaimants.type, getCreateTaskClaimantsSaga);
  yield takeLatest(getTaskTypeOptions.type, getTaskTypeOptionsSaga);
}
